/**
 * Stripe Product Service
 *
 * When Gobodo is on: listProducts() loads from Product__c (graph) first so RDS-synced
 * products appear; falls back to Stripe API if no graph records. getProduct() tries
 * graph by id then Stripe. Create/update/delete use Stripe API.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../gobodo/gobodoClient';
import { getRecordsForObject, getRecordById, type GenericRecord } from './recordStorageService';
import type { ProductCatalog } from '../types/index';

const PRODUCT_OBJECT = 'Product';

const PLACEHOLDER_CURRENCY = 'USD';
const PLACEHOLDER_BILLING_FREQUENCY = 'one-time' as const;

/** Normalize API response (StripeProduct or ProductResponse with .product) to a flat shape with id, name, description, active, metadata, created */
function normalizeStripeProduct(raw: any): {
  id: string;
  name: string;
  description: string;
  active: boolean;
  metadata: Record<string, string> | undefined;
  created: number;
} {
  const product = raw?.product ?? raw;
  const id = product?.id ?? raw?.id ?? '';
  const name = product?.name ?? raw?.name ?? '';
  const description = product?.description ?? raw?.description ?? '';
  const active = product?.active ?? raw?.active ?? true;
  const metadata = product?.metadata ?? raw?.metadata;
  const created = product?.created ?? raw?.created ?? 0;
  return { id, name, description, active, metadata, created };
}

/** Map Product__c GenericRecord (from graph/RDS) to ProductCatalog. */
function genericRecordToProductCatalog(record: GenericRecord): ProductCatalog {
  const name = record.Name__c ?? record.name ?? record.label ?? '';
  const now = new Date().toISOString();
  const isActiveRaw = record.IsActive__c ?? record.isActive;
  const isActive = isActiveRaw !== false && isActiveRaw !== 'false';
  const unitPrice = Number(record.UnitPrice__c ?? record.unitPrice ?? 0) || 0;
  const currency = (record.Currency__c ?? record.currency ?? 'USD').toString().toUpperCase();
  const category = (record.category as ProductCatalog['category']) ?? 'service';
  const billingFreq = (record.billingFrequency ?? record.BillingFrequency__c) as ProductCatalog['billingFrequency'] | undefined;
  return {
    id: String(record.id),
    name: String(name),
    productCode: String(record.ProductCode__c ?? record.productCode ?? ''),
    description: record.Description__c ?? record.description,
    category: ['service', 'subscription', 'one-time', 'addon'].includes(category) ? category : 'service',
    isActive,
    unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
    currency: currency || 'USD',
    billingFrequency: billingFreq ?? undefined,
    createdDate: record.createdDate ?? now,
    lastModifiedDate: record.lastModified ?? record.lastModifiedDate ?? now,
    createdBy: record.createdBy ?? 'system',
    lastModifiedBy: record.lastModifiedBy ?? 'system',
  };
}

/** Map Stripe product (or ProductResponse) to ProductCatalog. Uses placeholders for price fields (no Prices API in this phase). */
function stripeToProductCatalog(raw: any): ProductCatalog {
  const n = normalizeStripeProduct(raw);
  const meta = n.metadata && typeof n.metadata === 'object' ? n.metadata : {};
  const createdDate = n.created
    ? new Date(n.created * 1000).toISOString()
    : new Date().toISOString();
  return {
    id: n.id,
    name: n.name,
    productCode: meta.productCode ?? '',
    description: n.description || undefined,
    category: (meta.category as ProductCatalog['category']) ?? 'service',
    isActive: n.active,
    unitPrice: 0,
    currency: PLACEHOLDER_CURRENCY,
    billingFrequency: PLACEHOLDER_BILLING_FREQUENCY,
    createdDate,
    lastModifiedDate: createdDate,
    createdBy: 'system',
    lastModifiedBy: 'system',
  };
}

/** ProductInput DTO fields only; everything else is stored in metadata (values must be strings). */
const PRODUCT_INPUT_KEYS = new Set(['name', 'description', 'active', 'isActive']);

/** Build ProductInput body: map DTO fields and put all other payload fields into metadata (string values). */
function toProductInput(p: Partial<ProductCatalog> & { name: string; isActive: boolean }): {
  name: string;
  description: string;
  active: boolean;
  metadata: Record<string, string>;
  images: string[];
  categoryIds: number[];
} {
  const metadata: Record<string, string> = {};
  for (const [key, value] of Object.entries(p)) {
    if (PRODUCT_INPUT_KEYS.has(key) || value === undefined || value === null) continue;
    if (key === 'id' || key === 'createdDate' || key === 'lastModifiedDate' || key === 'createdBy' || key === 'lastModifiedBy') continue;
    metadata[key] = typeof value === 'string' ? value : String(value);
  }
  return {
    name: p.name,
    description: (p.description ?? '').toString(),
    active: p.isActive,
    metadata,
    images: [],
    categoryIds: [],
  };
}

export async function listProducts(): Promise<ProductCatalog[]> {
  try {
    const records = await getRecordsForObject(PRODUCT_OBJECT, { size: 50 });
    if (records.length > 0) {
      return records.map(genericRecordToProductCatalog);
    }
  } catch {
    // Fall through to Stripe API
  }
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const data = await api.listStripeProducts();
  const list = Array.isArray(data) ? data : [];
  return list.map((item: any) => stripeToProductCatalog(item));
}

export async function getProduct(productId: string): Promise<ProductCatalog> {
  try {
    const record = await getRecordById(PRODUCT_OBJECT, productId);
    if (record) {
      return genericRecordToProductCatalog(record);
    }
  } catch {
    // Fall through to Stripe API
  }
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const data = await api.getStripeProduct(productId);
  return stripeToProductCatalog(data);
}

export async function createProduct(
  payload: Omit<ProductCatalog, 'id' | 'createdDate' | 'lastModifiedDate' | 'createdBy' | 'lastModifiedBy'>
): Promise<ProductCatalog> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const body = toProductInput(payload);
  const input = new GobodoApi.ProductInput(body.name);
  input.setDescription(body.description);
  input.setActive(body.active);
  input.setMetadata(body.metadata);
  input.setImages(body.images);
  input.setCategoryIds(body.categoryIds);
  const data = await api.createStripeProduct(input);
  return stripeToProductCatalog(data);
}

export async function updateProduct(
  productId: string,
  updates: Partial<ProductCatalog>
): Promise<ProductCatalog> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  const existing = await getProduct(productId);
  const merged = { ...existing, ...updates };
  const body = toProductInput(merged);
  const input = new GobodoApi.ProductInput(body.name);
  input.setDescription(body.description);
  input.setActive(body.active);
  input.setMetadata(body.metadata);
  input.setImages(body.images);
  input.setCategoryIds(body.categoryIds);
  const data = await api.updateStripeProduct(productId, input);
  return stripeToProductCatalog(data);
}

export async function deleteProduct(productId: string): Promise<void> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client);
  await api.deleteStripeProduct(productId);
}
